"use client";

import BlogCard from "@/components/common/BlogCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { normalizeBlogPost } from "@/types/blogTypes";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCustomBlogs } from "@/services/api/customBlogApi";
import Loader from "@/components/common/Loader";

export default function BlogListClient() {
  const [activeTab, setActiveTab] = useState("all");

  const { data: blogResponse, isLoading, error } = useQuery({
    queryKey: ['blogs', 'list'],
    queryFn: () => getCustomBlogs(1, 100), // Fetching more to allow client-side filtering for now
  });

  const blogData = blogResponse?.results || [];

  // Transform WordPress API data to BlogPost format
  const transformedBlogs = useMemo(() => {
    if (!blogData || !Array.isArray(blogData)) return [];
    return blogData.map(normalizeBlogPost);
  }, [blogData]);

  // Filter blogs based on active tab
  const getBlogsByFilter = (filter: string) => {
    if (filter === "all") {
      return transformedBlogs;
    }
    if (filter === "news") {
      return transformedBlogs.filter(blog => blog.content_type === "news");
    }
    if (filter === "blogs") {
      return transformedBlogs.filter(blog => blog.content_type === "blog");
    }
    return transformedBlogs;
  };

  const currentBlogs = getBlogsByFilter(activeTab);

  if (error) {
    return (
      <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto py-mob-40 sm:py-tab-40 lg:py-web-73">
        <div className="text-center py-web-100">
          <p className="text-sm text-red-500">Error loading blog posts. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto py-mob-40 sm:py-tab-40 lg:py-web-73 lg:pt-web-40 font-satoshi">
      <Tabs
        defaultValue="all"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full mt-mob-30 sm:mt-tab-40 lg:mt-web-40"
      >
        <div
          className="flex flex-col sm:flex-row gap-mob-43 sm:gap-0 items-center sm:justify-between drop-shadow-sm text-center mb-mob-40 sm:mb-tab-65 lg:mb-web-60
             py-mob-46 sm:py-tab-40 lg:py-web-50 px-mob-20 sm:px-tab-40 lg:px-web-70 rounded-lg
             bg-[url('/images/header-text-wrapper-mob.webp')] sm:bg-[url('/images/header-text-wrapper.webp')]
             bg-cover bg-center"
        >
          <h2 className="text-mob-38 sm:text-tab-38 lg:text-web-50 leading-mob-55 sm:leading-tab-55 lg:leading-[120%] text-SecondaryYellow tracking-wider font-normal lg:font-normal capitalize">
            News And Blogs
          </h2>
          <TabsList className="border border-[#DADADA] rounded-lg w-fit flex p-mob-10 sm:p-tab-10 gap-mob-10 sm:gap-tab-10 lg:gap-web-10 lg:p-web-10 bg-black/20">
            <TabsTrigger
              value="all"
              className="px-mob-10 sm:px-tab-15 uppercase lg:px-web-10 py-mob-7 sm:py-tab-5 lg:py-web-5 !text-mob-14 sm:!text-tab-16 lg:!text-web-15 font-medium text-white data-[state=active]:bg-SecondaryYellow rounded-md data-[state=active]:text-PrimaryDarkBlue tracking-widest"
            >
              All
            </TabsTrigger>
            <TabsTrigger
              value="news"
              className="px-mob-10 sm:px-tab-15 uppercase lg:px-web-10 py-mob-7 sm:py-tab-5 lg:py-web-5 !text-mob-14 sm:!text-tab-16 lg:!text-web-15 font-medium text-white data-[state=active]:bg-SecondaryYellow rounded-md data-[state=active]:text-PrimaryDarkBlue tracking-widest"
            >
              News
            </TabsTrigger>
            <TabsTrigger
              value="blogs"
              className="px-mob-10 sm:px-tab-15 uppercase lg:px-web-10 py-mob-7 sm:py-tab-5 lg:py-web-5 !text-mob-14 sm:!text-tab-16 lg:!text-web-15 font-medium text-white data-[state=active]:bg-SecondaryYellow rounded-md data-[state=active]:text-PrimaryDarkBlue tracking-widest"
            >
              Blogs
            </TabsTrigger>
          </TabsList>
        </div>

        {isLoading && transformedBlogs.length === 0 && (
          <div className="text-center py-web-100 flex justify-center">
            <Loader />
          </div>
        )}

        <TabsContent value="all">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-tab-30 sm:gap-y-tab-50 lg:gap-web-50 lg:gap-y-web-60">
            {currentBlogs.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="news">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-tab-30 sm:gap-y-tab-50 lg:gap-web-50 lg:gap-y-web-60">
            {currentBlogs.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="blogs">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-tab-30 sm:gap-y-tab-50 lg:gap-web-50 lg:gap-y-web-60">
            {currentBlogs.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
